import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Panel, Overline, EmptyValue, SourceBadge, TrendChart } from "@/components/portal/Bits";
import { COLORS } from "@/lib/format";
import { Instagram } from "lucide-react";

function Stat({ label, value, sub }) {
  return (
    <Panel className="p-5">
      <Overline>{label}</Overline>
      <div className="font-display text-3xl text-white mt-2">{value ?? <EmptyValue label="No data yet" />}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Panel>
  );
}

export default function PublicMediaKit() {
  const { token } = useParams();
  const [kit, setKit] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/media-kit/${token}`);
        setKit(data);
      } catch {
        setError(true);
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background grain p-6 text-center">
        <Logo size={44} />
        <h1 className="font-display text-3xl text-white mt-8">Media kit unavailable</h1>
        <p className="text-muted-foreground mt-2">This kit isn't shared publicly, or the link has been revoked.</p>
      </div>
    );
  }
  if (!kit) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">Loading…</div>;

  const a = kit.athlete;
  const weekly = (kit.weekly || []).map((w) => ({ week: w.week, reach: w.reach, engagement_rate: w.engagement_rate }));

  return (
    <div className="min-h-screen bg-background grain">
      <div className="mx-auto max-w-4xl px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <Logo size={40} />
          <span className="overline">Brand-deal media kit</span>
        </div>

        <Panel className="p-8 mb-8 flex flex-col sm:flex-row items-start gap-6">
          {a.headshot_url
            ? <img src={a.headshot_url} alt={a.name} className="h-24 w-24 rounded-md object-cover object-top border border-border" />
            : <div className="h-24 w-24 rounded-md bg-secondary border border-border" />}
          <div>
            <h1 className="font-display-tight text-5xl text-white">{a.name}</h1>
            <p className="text-muted-foreground mt-2">{a.sport?.toUpperCase()} · {a.school}{a.position ? ` · ${a.position}` : ""}</p>
            {a.commitment && (
              <p className="text-sm text-[#E1FF00] mt-2 font-medium" data-testid="kit-commitment">Committed · {a.commitment}</p>
            )}
            {a.instagram_link && (
              <a href={a.instagram_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-white mt-3 hover:text-[#E1FF00] transition-colors">
                <Instagram className="h-4 w-4" /> @{a.instagram_handle}
              </a>
            )}
          </div>
        </Panel>

        {kit.has_social_data ? (
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <Stat label="Total following" value={kit.followers?.total} sub={kit.followers?.growth_pct != null ? `${kit.followers.growth_pct >= 0 ? "+" : ""}${kit.followers.growth_pct}% WoW` : null} />
            <Stat label="Weekly reach" value={kit.reach?.current} sub={kit.reach?.delta != null ? `${kit.reach.delta >= 0 ? "+" : ""}${kit.reach.delta} WoW` : null} />
            <Stat label="Engagement rate" value={kit.engagement?.rate != null ? `${kit.engagement.rate}%` : null} />
          </div>
        ) : (
          <Panel className="p-6 mb-8 border-dashed">
            <p className="text-sm text-muted-foreground">Verified reach and engagement metrics will appear here as they are recorded from authorized sources.</p>
          </Panel>
        )}

        {weekly.length >= 2 && (
          <Panel className="p-6 mb-8">
            <Overline className="mb-3">Reach over time</Overline>
            <TrendChart data={weekly} dataKey="reach" color={COLORS.neutral} />
          </Panel>
        )}

        {kit.stats?.length > 0 && (
          <Panel className="p-6 mb-8">
            <div className="flex items-center justify-between mb-3">
              <Overline>On-field statistics</Overline>
              {kit.stats_source && <SourceBadge source={kit.stats_source} updatedAt={kit.stats_updated_at} />}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {kit.stats.map((s, i) => (
                <div key={i} className="flex justify-between border-b border-border/50 py-2">
                  <span className="text-sm text-muted-foreground">{s.label}</span>
                  <span className="text-sm font-semibold text-white">{s.value}</span>
                </div>
              ))}
            </div>
          </Panel>
        )}

        <p className="text-xs text-muted-foreground/60 text-center mt-12">
          Shared with approval via The Merch Dash · Merch Run NIL
        </p>
      </div>
    </div>
  );
}
